const BASE = import.meta.env.VITE_ADMIN_API_URL ?? "";

function getToken(): string | null {
  try {
    const s = localStorage.getItem("sq-admin-auth");
    return s ? JSON.parse(s)?.state?.token ?? null : null;
  } catch { return null; }
}

function getAssumedRole(): string | null {
  try {
    const s = localStorage.getItem("sq-admin-auth");
    return s ? JSON.parse(s)?.state?.assumedRole ?? null : null;
  } catch { return null; }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const assumedRole = getAssumedRole();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(assumedRole ? { "X-Assumed-Role": assumedRole } : {}),
    ...(options.headers as Record<string, string> ?? {}),
  };
  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  const json = await res.json();
  if (!res.ok) throw Object.assign(new Error(json.error ?? "Request failed"), { status: res.status });
  return json.data as T;
}

const get  = <T>(path: string) => request<T>(path);
const post = <T>(path: string, body: unknown) => request<T>(path, { method: "POST", body: JSON.stringify(body) });
const patch= <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
const put  = <T>(path: string, body: unknown) => request<T>(path, { method: "PUT",   body: JSON.stringify(body) });
const del  = <T>(path: string) => request<T>(path, { method: "DELETE" });

export const adminApi = {
  sendOtp:    (email: string) => post("/admin/auth/otp/send", { email }),
  verifyOtp:  (email: string, code: string) =>
    post<{ token: string; role: string; permissions: string[] }>("/admin/auth/otp/verify", { email, code }),

  listFamilies: (search?: string) =>
    get<{ families: any[]; total: number }>(`/admin/families${search ? `?search=${encodeURIComponent(search)}` : ""}`),
  getFamily:    (familyId: string) =>
    get<{ family: any; kids: any[]; members: any[] }>(`/admin/families/${familyId}`),
  updateFamily: (familyId: string, body: Record<string, unknown>) =>
    patch(`/admin/families/${familyId}`, body),
  deleteFamily: (familyId: string) => del(`/admin/families/${familyId}`),

  kidPinReset: (familyId: string, kidId: string, pin: string) =>
    patch(`/admin/families/${familyId}/kids/${kidId}/pin-reset`, { pin }),
  kidBalance:  (familyId: string, kidId: string, delta: number, reason: string) =>
    patch(`/admin/families/${familyId}/kids/${kidId}/balance`, { delta, reason }),
  deleteKid:   (familyId: string, kidId: string) => del(`/admin/families/${familyId}/kids/${kidId}`),

  listCompletions:  (familyId: string) =>
    get<{ completions: any[] }>(`/admin/families/${familyId}/completions`),
  getPhotoUrl:      (familyId: string, completionId: string) =>
    get<{ url: string }>(`/admin/families/${familyId}/completions/${completionId}/photo-url`),
  deleteCompletion: (familyId: string, completionId: string) =>
    del(`/admin/families/${familyId}/completions/${completionId}`),
  listTransactions: (familyId: string, kidId?: string) =>
    get<{ transactions: any[] }>(`/admin/families/${familyId}/transactions${kidId ? `?kidId=${kidId}` : ""}`),

  exportBackup: (familyId: string) =>
    fetch(`${BASE}/admin/backup/export/${familyId}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken() ?? ""}` },
    }),
  backupDryRun: (backup: unknown) =>
    post<{ familyId: string; familyName: string; results: Record<string, any> }>("/admin/backup/dry-run", { backup }),
  backupRestore: (backup: unknown, confirmedFamilyName: string) =>
    post("/admin/backup/restore", { backup, confirmedFamilyName }),

  getMetrics:  (days = 7) => get<Record<string, any>>(`/admin/metrics?days=${days}`),
  listAudit:   (params?: { actorId?: string; familyId?: string; action?: string; limit?: number }) =>
    get<{ entries: any[]; total: number }>(`/admin/audit${params && Object.keys(params).length ? `?${new URLSearchParams(params as any)}` : ""}`),
  exportAudit: () =>
    fetch(`${BASE}/admin/audit/export`, { headers: { Authorization: `Bearer ${getToken() ?? ""}` } }),

  listTeam:   () => get<{ members: any[] }>("/admin/team"),
  inviteTeam: (email: string, role: string) => post("/admin/team", { email, role }),
  revokeTeam: (memberId: string) => del(`/admin/team/${memberId}`),

  getRoles:   () => get<{ roles: any[] }>("/admin/roles"),
  updateRole: (roleId: string, permissions: string[]) => put(`/admin/roles/${roleId}`, { permissions }),
};
